import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

const langKey = (lang: string): 'pt' | 'de' | 'en' => {
  if (lang === 'de') return 'de';
  if (lang === 'en') return 'en';
  return 'pt';
};

export default function ArticleDetailPage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lk = langKey(i18n.language);
  const prefix = i18n.language === 'pt' ? '' : '/' + i18n.language;

  const article = articles.find(a => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-32 pb-20 px-4 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">
            {t('article.breadcrumbHome')}
          </h1>
          <Link to={prefix + '/'} className="btn-primary text-sm">
            {t('article.breadcrumbHome')}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const related = articles.filter(a => a.slug !== article.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        {/* Breadcrumb + Hero */}
        <section className="pt-24 md:pt-28 pb-8 md:pb-12 bg-background-50 border-b border-background-200">
          <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6">
              <Link to={prefix + '/'} className="hover:text-primary-600 transition-colors">
                {t('article.breadcrumbHome')}
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">{article.title[lk]}</span>
            </nav>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-foreground-950 leading-tight mb-4">
              {article.title[lk]}
            </h1>

            <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500 mb-4">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {t('article.updated')}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {t('article.author')}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-shield-check-line"></i>
                {t('article.reviewed')}
              </span>
            </div>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
          <article>
            {/* Intro / excerpt */}
            <p className="text-lg text-foreground-700 leading-relaxed mb-8 md:mb-10 border-l-4 border-primary-500 pl-5 py-1">
              {article.excerpt[lk]}
            </p>

            {/* Sections */}
            {article.sections.map((section, idx) => (
              <section key={idx} className="mb-10 md:mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  {section.heading[lk]}
                </h2>
                <p className="text-base text-foreground-700 leading-relaxed mb-6">
                  {section.body[lk]}
                </p>
              </section>
            ))}

            {/* Internal link back to home */}
            <div className="mb-8 md:mb-10">
              <Link to={prefix + '/'} className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors">
                <i className="ri-home-line mr-1"></i>
                {t('article.breadcrumbHome')}
              </Link>
            </div>

            {/* Bottom CTA */}
            <div className="bg-gradient-to-br from-primary-50 to-accent-50/50 rounded-xl p-6 md:p-8 text-center mb-10">
              <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-3">
                CSSBuy Spreadsheet
              </h3>
              <p className="text-sm text-foreground-600 mb-5 max-w-lg mx-auto">
                {t('cta.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="btn-primary text-sm"
                >
                  {t('article.ctaMain')}
                  <i className="ri-external-link-line ml-1.5"></i>
                </a>
                <Link to={prefix + '/'} className="btn-secondary text-sm">
                  <i className="ri-home-line mr-1.5"></i>
                  {t('article.breadcrumbHome')}
                </Link>
              </div>
            </div>

            {/* Related articles */}
            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                {t('pillars.title')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {related.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={prefix + '/article/' + rel.slug}
                    className="group card p-5 hover:shadow-sm transition-all"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 mb-3">
                      <i className={rel.icon}></i>
                    </div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1 group-hover:text-primary-600 transition-colors leading-snug">
                      {rel.title[lk]}
                    </h3>
                  </Link>
                ))}
              </div>
            </section>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
}